import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiUrl } from "../service/apiUrl";
import {
  fetchUserDetails,
  uploadProfileImageAction,
  setUserDetails as setUserDetailsAction,
} from "../actions/authActions";
// Import update user details service method
import { updateUserDetails, changeUserPassword } from "../service/authService";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Avatar,
  IconButton,
  Paper,
  Alert,
  Snackbar,
  Grid,
  Divider,
  InputAdornment,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
// Import departmentService to fetch department and location data like Registration form
import departmentService from "../service/departmentService";
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Person,
  Phone,
  Business,
  LocationOn,
  Badge,
  Email,
  Lock,
  CloudUpload,
  Delete,
  Visibility,
  VisibilityOff,
  AssignmentInd,
} from "@mui/icons-material";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { useNavigate } from "react-router-dom";

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get user data from Redux store
  const {
    serviceNo,
    userData,
    userImage,
    loading: authLoading,
    employeeName,
    department,
    role,
  } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  // State for department and location dropdown options (fetched from API like Registration form)
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    ServiceNo: "",
    Name: "",
    MobileNo: "",
    Department: "",
    company: "",
    role: "",
    Location: "",
    SLocation: "",
    status: "A",
    qr: "N",
  });

  // Password change form
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  // Fetch user details on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (serviceNo) {
        setLoading(true);
        try {
          // If we already have userData in Redux, use it
          if (userData) {
            setFormData({
              ServiceNo: userData.ServiceNo || serviceNo || "",
              Name: userData.Name || employeeName || "",
              MobileNo: userData.MobileNo || "",
              // Ensure Department and Location store codes, not names
              Department: userData.Department || department || "",
              company: userData.company || "",
              role: userData.role || role || "",
              Location: userData.Location || "",
              SLocation: userData.SLocation || "",
              // Ensure status is always "A" or "I", not "Active"/"Inactive"
              status:
                userData.status === "A" || userData.status === "I"
                  ? userData.status
                  : "A",
              qr: userData.qr || "N",
            });

            // Load user images
            loadUserImages(userData.ServiceNo || serviceNo);
          } else {
            // Fetch fresh user details
            const result = await dispatch(fetchUserDetails(serviceNo));
            if (result.success && result.data) {
              const userData = result.data;
              setFormData({
                ServiceNo: userData.ServiceNo || serviceNo,
                Name: userData.Name || "",
                MobileNo: userData.MobileNo || "",
                // Ensure Department and Location store codes, not names
                Department: userData.Department || "",
                company: userData.company || "",
                role: userData.role || "",
                Location: userData.Location || "",
                SLocation: userData.SLocation || "",
                // Ensure status is always "A" or "I", not "Active"/"Inactive"
                status:
                  userData.status === "A" || userData.status === "I"
                    ? userData.status
                    : "A",
                qr: userData.qr || "N",
              });

              // Load user images
              loadUserImages(userData.ServiceNo || serviceNo);
            }
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
          setErrorMessage("Failed to load user details");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [serviceNo, dispatch, userData, employeeName, department, role]);

  // Fetch department and location options when edit mode is activated (same as Registration form)
  useEffect(() => {
    if (editMode) {
      // Fetch departments from API using departmentService (same as Registration form)
      const fetchDepartments = async () => {
        setLoadingDepartments(true);
        try {
          const departments = await departmentService.getDepartments();
          setDepartmentOptions(departments);
          console.log("Fetched departments:", departments);
        } catch (error) {
          console.error("Error fetching departments:", error);
          setErrorMessage("Failed to load departments");
        } finally {
          setLoadingDepartments(false);
        }
      };

      // Fetch locations from API (same endpoint as Registration form)
      const fetchLocations = async () => {
        setLoadingLocations(true);
        try {
          const response = await fetch(
            apiUrl("UserDetails/Locations"),
          );
          const data = await response.json();
          if (data.statusCode === 200 && data.resultSet) {
            setLocationOptions(data.resultSet);
            console.log("Fetched locations:", data.resultSet);
          }
        } catch (error) {
          console.error("Error fetching locations:", error);
          setErrorMessage("Failed to load locations");
        } finally {
          setLoadingLocations(false);
        }
      };

      // Call both fetch functions
      fetchDepartments();
      fetchLocations();
    }
  }, [editMode]);

  // Load user images
  const loadUserImages = (serviceNo) => {
    if (!serviceNo) return;

    // Create image URLs with timestamps to avoid caching
    const timestamp = new Date().getTime();

    const imageUrls = [
      apiUrl(`User/GetUserImg?Sno=${serviceNo}&t=${timestamp}`),
      // You can add more image URLs here if user has multiple images
    ];

    setImages(imageUrls);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle password input changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    const requiredFields = [
      "Name",
      "MobileNo",
      "Department",
      "company",
      "Location",
    ];
    requiredFields.forEach((field) => {
      if (!formData[field] || !formData[field].toString().trim()) {
        newErrors[field] = "Required";
      }
    });

    if (formData.MobileNo && formData.MobileNo.length < 10) {
      newErrors.MobileNo = "10 digits required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate password form
  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = "Required";
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = "Required";
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = "Minimum 8 characters required";
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = "Required";
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !serviceNo) return;

    // Validate file
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setErrorMessage(
        "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("Image size should not exceed 5MB");
      return;
    }

    setUploadingImage(true);

    try {
      const result = await dispatch(uploadProfileImageAction(serviceNo, file));

      if (result.success) {
        setSuccessMessage("Profile picture updated successfully!");

        // Reload images
        loadUserImages(serviceNo);

        // Update form data if this is the first image
        if (images.length === 0) {
          setFormData((prev) => ({
            ...prev,
            profilePicture: result.imageUrl,
          }));
        }
      } else {
        setErrorMessage(result.error || "Failed to upload image");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMessage("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setUpdating(true);
    setErrorMessage("");

    try {
      // Prepare update data using same structure as registration
      const updateData = {
        ServiceNo: formData.ServiceNo,
        Name: formData.Name,
        MobileNo: formData.MobileNo,
        Department: formData.Department,
        company: formData.company,
        // Only admins can change role — non-admins keep their original role
        role: role === "A" ? formData.role : (userData?.role || role || "U"),
        Location: formData.Location,
        SLocation: formData.SLocation,
        status: formData.status,
        qr: formData.qr,
      };

      // Call service method which uses axios (handles CORS properly)
      const result = await updateUserDetails(updateData);

      if (result.success) {
        setSuccessMessage("Profile updated successfully!");

        // Update Redux store
        dispatch({
          type: "SET_USER_DETAILS",
          payload: updateData,
        });

        // Update localStorage
        localStorage.setItem("employeeName", updateData.Name || "");
        localStorage.setItem("department", updateData.Department || "");
        localStorage.setItem("role", updateData.role || "");

        setEditMode(false);
      } else {
        setErrorMessage(result.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Update error:", error);
      setErrorMessage(
        error?.Message || error?.message || "Failed to update profile",
      );
    } finally {
      setUpdating(false);
    }
  };

  // Handle password change
  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setUpdating(true);
    setErrorMessage("");

    try {
      // Call service method which uses axios (handles CORS properly)
      const result = await changeUserPassword(
        serviceNo,
        passwordData.currentPassword,
        passwordData.newPassword,
      );

      if (result.success) {
        setSuccessMessage("Password changed successfully!");
        setPasswordDialogOpen(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        setErrorMessage(result.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Password change error:", error);
      setErrorMessage(
        error?.Message || error?.message || "Failed to change password",
      );
    } finally {
      setUpdating(false);
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    // Reset form to original data
    if (userData) {
      setFormData({
        ServiceNo: userData.ServiceNo || serviceNo || "",
        Name: userData.Name || employeeName || "",
        MobileNo: userData.MobileNo || "",
        // Ensure Department and Location store codes, not names
        Department: userData.Department || department || "",
        company: userData.company || "",
        role: userData.role || role || "",
        Location: userData.Location || "",
        SLocation: userData.SLocation || "",
        // Ensure status is always "A" or "I", not "Active"/"Inactive"
        status:
          userData.status === "A" || userData.status === "I"
            ? userData.status
            : "A",
        qr: userData.qr || "N",
      });
    }
    setEditMode(false);
    setErrors({});
  };

  // Navigate to next/previous image
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  };

  // Handle image click to open dialog
  const handleImageClick = () => {
    if (images.length > 0) {
      setImageDialogOpen(true);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 0, margin: "0 auto" }}>
      <HeaderBannerPage />

      <Box
        sx={{ padding: "20px", maxWidth: "800px", margin: "0 auto", mt: -5 }}
      >
        <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#0C3C6D" }}
            >
              User Profile
            </Typography>

            {!editMode ? (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setEditMode(true)}
                sx={{
                  bgcolor: "#1e0153",
                  "&:hover": { bgcolor: "#333" },
                }}
              >
                Edit Profile
              </Button>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  sx={{ borderColor: "#f44336", color: "#f44336" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSubmit}
                  loading={updating}
                  sx={{
                    bgcolor: "#1e0153",
                    "&:hover": { bgcolor: "#333" },
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            )}
          </Box>

          {/* Success/Error Messages */}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Left Column - Profile Image */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <Avatar
                      src={
                        images[currentImageIndex] || "/placeholder-avatar.png"
                      }
                      sx={{
                        width: 150,
                        height: 150,
                        border: "3px solid #1e0153",
                        cursor: images.length > 0 ? "pointer" : "default",
                      }}
                      onClick={handleImageClick}
                    />

                    {editMode && (
                      <IconButton
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          bgcolor: "#1e0153",
                          color: "white",
                          "&:hover": { bgcolor: "#333" },
                        }}
                        component="label"
                      >
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                        />
                        {uploadingImage ? (
                          <CircularProgress size={24} />
                        ) : (
                          <PhotoCamera />
                        )}
                      </IconButton>
                    )}
                  </Box>

                  {images.length > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={prevImage}
                        disabled={uploadingImage}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={nextImage}
                        disabled={uploadingImage}
                      >
                        Next
                      </Button>
                    </Box>
                  )}

                  <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
                    {formData.Name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Service No: {formData.ServiceNo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Role:{" "}
                    {formData.role === "A"
                      ? "Admin"
                      : formData.role === "S"
                        ? "Supervisor"
                        : "User"}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Lock />}
                    onClick={() => setPasswordDialogOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Form Fields */}
            <Grid item xs={12} md={8}>
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Service Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Service Number"
                      name="ServiceNo"
                      value={formData.ServiceNo}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Badge />
                          </InputAdornment>
                        ),
                        readOnly: true,
                      }}
                      disabled
                      variant={editMode ? "outlined" : "filled"}
                    />
                  </Grid>

                  {/* Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="Name"
                      value={formData.Name}
                      onChange={handleChange}
                      error={!!errors.Name}
                      helperText={errors.Name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person />
                          </InputAdornment>
                        ),
                        readOnly: !editMode,
                      }}
                      disabled={!editMode}
                      variant={editMode ? "outlined" : "filled"}
                    />
                  </Grid>

                  {/* Mobile Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      name="MobileNo"
                      value={formData.MobileNo}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/[^0-9]/g, "")
                          .slice(0, 10);
                        setFormData((prev) => ({ ...prev, MobileNo: value }));
                      }}
                      error={!!errors.MobileNo}
                      helperText={errors.MobileNo}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                        readOnly: !editMode,
                      }}
                      disabled={!editMode}
                      variant={editMode ? "outlined" : "filled"}
                    />
                  </Grid>

                  {/* Department Autocomplete - Fetched from API like Registration form */}
                  <Grid item xs={12} sm={6}>
                    {editMode ? (
                      <Autocomplete
                        disabled={!editMode || loadingDepartments}
                        options={departmentOptions}
                        getOptionLabel={(option) => option?.depName || ""}
                        value={
                          departmentOptions.find(
                            (d) => d?.depCode === formData.Department,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            Department: newValue?.depCode || "",
                          }));
                        }}
                        loading={loadingDepartments}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Department"
                            error={!!errors.Department}
                            helperText={
                              errors.Department ||
                              (loadingDepartments ? "Loading..." : "")
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Business />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Department"
                        value={formData.Department}
                        disabled
                        variant="filled"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>

                  {/* Company */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      error={!!errors.company}
                      helperText={errors.company}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business />
                          </InputAdornment>
                        ),
                        readOnly: !editMode,
                      }}
                      disabled={!editMode}
                      variant={editMode ? "outlined" : "filled"}
                    />
                  </Grid>

                  {/* Role — only admins can change this */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      error={!!errors.role}
                      helperText={role !== "A" && editMode ? "Only admins can change the role" : errors.role}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AssignmentInd />
                          </InputAdornment>
                        ),
                        readOnly: !editMode || role !== "A",
                      }}
                      disabled={!editMode || role !== "A"}
                      variant={editMode ? "outlined" : "filled"}
                    >
                      <MenuItem value="A">Admin</MenuItem>
                      <MenuItem value="U">User</MenuItem>
                      <MenuItem value="S">Supervisor</MenuItem>
                    </TextField>
                  </Grid>

                  {/* Location Autocomplete - Fetched from API like Registration form */}
                  <Grid item xs={12} sm={6}>
                    {editMode ? (
                      <Autocomplete
                        disabled={!editMode || loadingLocations}
                        options={locationOptions}
                        getOptionLabel={(option) => option?.locName || ""}
                        value={
                          locationOptions.find(
                            (l) => l?.locCode === formData.Location,
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            Location: newValue?.locCode || "",
                          }));
                        }}
                        loading={loadingLocations}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Location"
                            error={!!errors.Location}
                            helperText={
                              errors.Location ||
                              (loadingLocations ? "Loading..." : "")
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationOn />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Location"
                        value={formData.Location}
                        disabled
                        variant="filled"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>

                  {/* Sub Location */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sub Location"
                      name="SLocation"
                      value={formData.SLocation}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn />
                          </InputAdornment>
                        ),
                        readOnly: !editMode,
                      }}
                      disabled={!editMode}
                      variant={editMode ? "outlined" : "filled"}
                    />
                  </Grid>

                  {/* Status */}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === "A"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? "A" : "I",
                            }))
                          }
                          disabled={!editMode}
                          color="primary"
                        />
                      }
                      label={formData.status === "A" ? "Active" : "Inactive"}
                    />
                  </Grid>

                  {/* QR Access */}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.qr === "Y"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              qr: e.target.checked ? "Y" : "N",
                            }))
                          }
                          disabled={!editMode}
                          color="secondary"
                        />
                      }
                      label="QR Access Enabled"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Additional Information */}
              {/* <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Additional Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Account Created:
                      </Typography>
                      <Typography variant="body1">
                        {userData?.created_date || "Not available"}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Last Updated:
                      </Typography>
                      <Typography variant="body1">
                        {userData?.updated_date || "Not available"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent> */}
              {/* </Card> */}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>Profile Image</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", p: 2 }}>
            <img
              src={images[currentImageIndex]}
              alt="Profile"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                borderRadius: "8px",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {images.length > 1 && (
            <>
              <Button onClick={prevImage}>Previous</Button>
              <Button onClick={nextImage}>Next</Button>
            </>
          )}
          <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Current Password"
              name="currentPassword"
              type={showPassword ? "text" : "password"}
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              error={!!passwordErrors.currentPassword}
              helperText={passwordErrors.currentPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="New Password"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              error={!!passwordErrors.newPassword}
              helperText={passwordErrors.newPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={updating}
            sx={{ bgcolor: "#1e0153", "&:hover": { bgcolor: "#333" } }}
          >
            {updating ? "Changing..." : "Change Password"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={!!successMessage || !!errorMessage}
        autoHideDuration={3000}
        onClose={() => {
          setSuccessMessage("");
          setErrorMessage("");
        }}
      />
    </Box>
  );
};

export default UserProfile;
